/**
 * MemberContainer
 * --
 */
import React from 'react';
import MemberPresenter from './MemberPresenter';
import * as XLSX from 'xlsx';
import MessageAlert, { DEPLOY_TYPE, DOMAIN_URL, removeCookie } from 'utils';
import { openAuthPopup } from 'utils/NiceAPIManager';
import { useDuplicateCheck, useFindAccount, useVerify } from 'hooks/AuthHooks';
import Formatter from 'utils/Formatter';
import dayjs from 'dayjs';
import { useCreateUser, useUpdateGuestUser } from 'hooks/UserHooks';
import { useSendEmail } from 'hooks/SendHooks';
import { useGetGrades } from 'hooks/GradeHooks';
import validateCheck, { passwordCheck } from 'utils/Validation';
import { useSearchParams } from 'react-router-dom';

const BUCKET_KEY = process.env.REACT_APP_MEMBER_EXCEL_URL;

const USER_KEY = {
  birthday: '생년월일',
  user_name: '이름',
  user_phone: '휴대폰번호',
  grade_name: '변경등급',
  created_at: '회원가입일',
  postcode: '우편번호',
  user_address: '주소1',
  user_address_detail: '주소2',
};

const MemberContainer = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const { navigate } = props;
  const [searchParams] = useSearchParams();

  const deviceType = searchParams.get('type') ?? 'PC';

  /* ===== Mutate ===== */
  const { createUser } = useCreateUser();
  const { updateGuestUser } = useUpdateGuestUser();
  const { findAccount } = useFindAccount();
  // 중복검사
  const { duplicate } = useDuplicateCheck();
  // 이메일 인증
  const { sendEmail } = useSendEmail();

  /* ===== STATE ===== */
  const timerStart = React.useRef(null);
  // 엑셀파일
  const [excelData, setExcelData] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  // 찾은 회원 데이터
  const [type, setType] = React.useState(null);

  // 입력값
  const [inputValue, setInputValue] = React.useState({
    user_account: '',
    password: '',
    re_password: '',
  });
  // 변경할 등급
  const [gradeIdx, setGradeIdx] = React.useState(6);

  // 본인인증 진행중인지 여부
  const [isProgress, setIsProgress] = React.useState(false);
  // 나이스 인증 EncodeData
  const [encodeData, setEncodeData] = React.useState('');
  // 중복검사 실행여부
  const [isDuplicate, setIsDuplicate] = React.useState({
    user_account: false,
    user_email: false,
  });

  // Checkbox 입력값
  const [checkedAll, setCheckedAll] = React.useState(false);
  const [checkedList, setCheckedList] = React.useState({
    terms_of_use: false, // 이용약관 동의
    terms_of_privacy: false, // 개인정보 수집 및 이용 동의
    terms_of_marketing: false, // 마케팅 목적의 개인정보 수집 및 이용 동의
    terms_of_ad: false, // 혜택 및 광고성 정보 수신 동의
    terms_of_age: false, // 만 14세 이상입니다
    marketing_consent: false, // 마케팅정보 수집 및 활용 동의
  });

  /* ===== Data ===== */
  // 나이스 사용자 정보 조회
  const { data: verify } = useVerify(
    encodeData && {
      EncodeData: encodeURIComponent(encodeData),
    }
  );
  const { data: Grades } = useGetGrades(null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return [];
      else return data;
    },
    initialData: [],
  });

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 라우트 핸들러
   * --
   * @param {string} t
   * @returns
   */
  const handleNavigate = (t = '') => {
    if (!t) return;

    switch (t) {
      case 'AUTH':
        setIsProgress(!isProgress);
        // 나이스 본인인증 팝업 열기
        openAuthPopup();
        return;
      case 'BACK':
        if (deviceType === 'mobile') {
          // 모바일
          setTimeout(() => (window.location.href = 'jmcoffee://signin'), 500);
        } else {
          // PC
          navigate(`/signup`);
        }
        return;
      default:
        return;
    }
  };

  /**
   * 데이터 바인딩 함수
   * --
   */
  const handleChange = (t, v, callback = null) => {
    setInputValue((prevState) => {
      const newData = { ...prevState, [t]: v };

      if (callback) callback(v);

      return newData;
    });
  };

  /**
   * 기존 회원 유효성 검사
   * --
   * @param {object} decode
   * @returns
   */
  const handleValidateCheck = async (decode) => {
    const { user_name, user_phone } = decode;

    try {
      // NOTE: 기존 회원인지 체크
      const isExist = excelData.find(
        (member) =>
          member[USER_KEY['user_name']] === user_name &&
          member[USER_KEY['user_phone']] === user_phone
      );

      // NOTE: 기존 회원이 아닐 경우 리턴
      if (!isExist) {
        window.alert('기존 회원이 아닙니다!. 신규 회원가입 후 이용해주세요!');
        return;
      }

      // NOTE: 변경할 등급 idx
      const findGrade =
        Grades.find((g) => g.grade_name === isExist[USER_KEY['grade_name']])
          ?.grade_id ?? 6;
      setGradeIdx(findGrade);

      // NOTE: 휴대폰 번호로 이미 가입되어 있는 회원인지 체크
      const { status, data } = await findAccount({
        user_name: user_name,
        user_phone: user_phone,
        type: 'USER',
        auth_type: 'GRADE',
      });

      // NOTE: 유저 호출 실패 처리
      if (status !== 400 && status !== 200) {
        MessageAlert.warning('인증에 실패하였습니다. 네트워크를 확인해주세요.');
        return;
      }

      const { 우편번호, 주소1, 주소2 } = isExist;
      let exist = {};

      if (우편번호 && 주소1) {
        exist = {
          postcode: `${우편번호}`,
          user_address: 주소1,
          user_address_detail: 주소2 ?? '',
        };
      }

      // NOTE: 회원테이블에 데이터가 없을 경우 신규 회원가입
      if (status === 400) {
        setInputValue((prev) => ({ ...prev, ...decode, ...exist }));
        setType('NEW');
        return;
      }

      if (!data) {
        window.alert('인증에 실패하였습니다. 네트워크를 확인해주세요.');
        return;
      }

      // NOTE: 쿠키 저장 시간 설정
      const saveTime = 5 * 60 * 1000; // 5m
      removeCookie('JM_COFFEE_ACCESS_TOKEN', data, saveTime);

      // NOTE: 회원가입이 되어 있을 경우
      setInputValue((prev) => ({ ...prev, ...exist }));
      handleSubmit('MEMBER');
    } catch (err) {
      console.error('[handleValidateCheck] Error!! ', err);
    } finally {
      setIsProgress(false); // 인증 진행 상태를 false로 설정
    }
  };

  /**
   * 중복검사
   * --
   * @param {string} t
   * @param {string | number} v
   */
  const handleDuplicate = async (t, v) => {
    if (t === 'user_account') {
      // 아이디 유효성 검사
      if (!v || v.length < 4 || v.length > 20) {
        MessageAlert.info(
          '아이디는 4자리 이상 20자리 이하로 입력하여야합니다.'
        );
        return false;
      }

      if (!validateCheck('account', v)) {
        MessageAlert.info('아이디는 영문자와 숫자만 입력 가능합니다.');
        return false;
      }
    }
    try {
      const duplicateData = {
        [t]: v,
        type: 'USER',
      };

      const type = {
        user_account: '아이디',
        user_email: '이메일',
      };

      const { data } = await duplicate(duplicateData);

      if (!data) {
        MessageAlert.warning(`사용할 수 없는 ${type[t]}입니다.`);
        return false;
      } else {
        const confirm = window.confirm(
          `사용가능한 ${type[t]}입니다. 사용하시겠습니까?`
        );

        if (!confirm) return false;

        setIsDuplicate((prev) => ({
          ...prev,
          [t]: true,
        }));

        return true;
      }
    } catch (err) {
      console.error('[Duplicate Check] ERROR : ', err);
      MessageAlert.error('중복 확인 중 오류가 발생하였습니다!');
      return false;
    }
  };

  /**
   * 회원 생성 유효성 검사
   * --
   * @returns {boolean}
   */
  const handleSubmitValidateCheck = () => {
    const { password, re_password } = inputValue;

    // 아이디 중복검사 여부
    if (!isDuplicate['user_account']) {
      MessageAlert.info('아이디 중복검사를 실행해주세요.');
      return false;
    }

    // 비밀번호 입력 여부
    if (!password) {
      MessageAlert.info('비밀번호를 입력해주세요.');
      return false;
    }

    // 비밀번호 확인 입력 여부
    if (!re_password) {
      MessageAlert.info('비밀번호를 한번 더 입력해주세요.');
      return false;
    }

    // 비밀번호 일치 여부
    if (password !== re_password) {
      MessageAlert.info('비밀번호가 일치하지 않습니다.');
      return;
    }

    // 비밀번호 유효성검사 통과 여부
    if (!passwordCheck(password)) {
      return false;
    }

    // NOTE: 필수 동의 여부 체크
    const checkRequired = checkListItem.some(
      (_item) => _item.required && !checkedList[_item.key]
    );
    if (checkRequired) {
      MessageAlert.info('필수 약관에 모두 동의해주세요.');
      return false;
    }

    return true;
  };

  /**
   * 기존 회원 생성/수정
   * --
   * @param {string} type NEW | MEMBER
   */
  const handleSubmit = async (type) => {
    try {
      let response;
      switch (type) {
        case 'NEW':
          if (!handleSubmitValidateCheck()) return;

          const confirm = window.confirm('입력하신 정보로 연동하시겠습니까?');

          if (!confirm) return;

          const { re_password, birthday, ...rest } = inputValue;

          const createData = {
            ...rest,
            grade_id: gradeIdx,
            user_setting: {
              ...rest?.user_setting,
              ...checkedList,
            },
          };

          response = await createUser(createData);
          break;
        case 'MEMBER':
          response = await updateGuestUser({
            grade_id: gradeIdx,
          });

          if (response.status !== 200) {
            window.alert(response.message);
            return;
          }
          break;
        default:
          window.alert('기존 회원연동에 실패하였습니다.');
          return;
      }

      if (response.status !== 200) {
        window.alert('기존 회원연동에 실패하였습니다.');
        return;
      }

      window.alert('성공적으로 연동되었습니다.');

      if (deviceType === 'mobile') {
        // 모바일
        setTimeout(() => (window.location.href = 'jmcoffee://signin'), 500);
      } else {
        // PC
        navigate(`/signin`);
      }
    } catch (err) {
      console.error('[handleSubmit] Error!! ', err);
      MessageAlert.error('기존 회원연동에 실패하였습니다.');
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  /**
   * 전체 선택
   * --
   */
  const handleCheckedAll = (c) => {
    const checkedObj = Object.entries(checkedList).reduce(
      (acc, [key, _]) => ({
        ...acc,
        [key]: c,
      }),
      {}
    );

    setCheckedAll(c);
    setCheckedList(checkedObj);
  };

  /**
   * 체크박스 선택
   * --
   */
  const handleChecked = (t, c) => {
    setCheckedList((prev) => ({
      ...prev,
      [t]: c,
    }));
  };

  /* ===== HOOKS ===== */
  // 체크박스 아이템
  const checkListItem = React.useMemo(() => {
    const items = [
      {
        key: 'terms_of_use',
        label: '이용약관 동의',
        value: '이용약관 동의',
        required: true,
        url: '',
      },
      {
        key: 'terms_of_privacy',
        label: '개인정보 수집 및 이용 동의',
        value: '개인정보 수집 및 이용 동의',
        required: true,
        url: '',
      },
      {
        key: 'terms_of_marketing',
        label: '마케팅 목적의 개인정보 수집 및 이용 동의',
        value: '마케팅 목적의 개인정보 수집 및 이용 동의',
        url: '',
      },
      {
        key: 'terms_of_ad',
        label: '혜택 및 광고성 정보 수신 동의',
        value: '혜택 및 광고성 정보 수신 동의',
        url: '',
      },
      {
        key: 'terms_of_age',
        label: '만 14세 이상입니다',
        value: '만 14세 이상입니다',
        required: true,
        url: '',
      },
    ];

    return items;
  }, []);

  /**
   * 기존 회원 데이터 호출
   * --
   */
  React.useEffect(() => {
    setIsLoading(true);

    fetch(BUCKET_KEY)
      .then((response) => {
        if (!response.ok) {
          MessageAlert.error('네트워크 응답이 올바르지 않습니다.');
          setTimeout(() => navigate('/main'), 500);
        }
        // ArrayBuffer 형태로 읽어오기
        return response.arrayBuffer();
      })
      .then((buffer) => {
        // ArrayBuffer를 이용해 워크북 생성
        const workbook = XLSX.read(buffer, { type: 'array' });

        // 첫 번째 시트 선택
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // 시트를 JSON으로 변환
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { range: 1 });

        // 회원가입일을 기준으로 역순 정렬
        const sortJsonData = jsonData.sort(
          (a, b) => dayjs(b['생년월일']).unix() - dayjs(a['생년월일']).unix()
        );

        setExcelData(sortJsonData);
      })
      .catch((error) => {
        MessageAlert.error('네트워크 응답이 올바르지 않습니다.');
        console.error('error!! ', error);
        setTimeout(() => navigate('/main'), 500);
      })
      .finally(() => {
        setTimeout(() => setIsLoading(false), 1000);
      });
    // eslint-disable-next-line
  }, []);

  /**
   * 본인인증 팝업 메세지 감지
   * --
   */
  React.useEffect(() => {
    const port = DEPLOY_TYPE === 'local' ? ':3000' : '';

    const handleMessage = (event) => {
      const domain =
        DEPLOY_TYPE === 'development'
          ? `mall-jm-coffee.${DOMAIN_URL}`
          : `mall.${DOMAIN_URL}`;

      // 이벤트의 출처를 확인하여 신뢰할 수 있는 출처에서 온 메시지만 처리
      if (
        event.origin === `${window.location.protocol}//${DOMAIN_URL}${port}` ||
        event.origin === `${window.location.protocol}//${domain}${port}`
      ) {
        const data = event.data;

        // AUTH 타입의 메시지인 경우
        if (data && data.type === 'AUTH') {
          setEncodeData(data.data); // 데이터를 상태로 설정
        } else {
          setIsProgress(false); // 인증 진행 상태를 false로 설정
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      setIsLoading(false);
      // 컴포넌트가 언마운트될 때 메시지 리스너를 제거
      window.removeEventListener('message', handleMessage);
    };
  }, [isProgress]);

  /**
   * 본인인증 데이터 디코딩
   * --
   */
  React.useEffect(() => {
    if (verify) {
      const { status, data } = verify;

      if (status === 200) {
        const { name, gender, mobileno, birthdate } = data;

        const phoneValue = Formatter.hyphen(mobileno);

        const GENDER = {
          0: 'F',
          1: 'M',
        };

        const date = `${birthdate.slice(0, 4)}.${birthdate.slice(
          4,
          6
        )}.${birthdate.slice(6, 8)}`;

        const decodeData = {
          user_name: name,
          user_gender: GENDER[gender],
          user_phone: phoneValue,
          birthday: date,
          user_birthday: dayjs(date).startOf('day').unix(),
        };

        handleValidateCheck(decodeData);
      } else {
        setIsProgress(false); // 인증 진행 상태를 false로 설정
      }
    }
    // eslint-disable-next-line
  }, [verify]);

  /**
   * 전체 선택 감지
   * --
   */
  React.useEffect(() => {
    const isAllCheck = Object.entries(checkedList).some(
      ([key, value]) => value === false
    );

    if (isAllCheck) {
      setCheckedAll(false);
    } else {
      setCheckedAll(true);
    }
  }, [checkedList, checkListItem]);

  /* ===== RENDER ===== */
  return (
    <>
      <MemberPresenter
        type={type}
        inputValue={inputValue}
        isLoading={isLoading}
        isDuplicate={isDuplicate}
        onNavigate={handleNavigate}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onDuplicate={handleDuplicate}
        checkedAll={checkedAll}
        checkedList={checkedList}
        onChecked={handleChecked}
        onCheckedAll={handleCheckedAll}
        checkListItem={checkListItem}
      />
    </>
  );
};

export default MemberContainer;
